import React, { useState, useMemo } from 'react';
import { Box, Grid, Paper, Typography, Pagination } from '@mui/material';
import { useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BlogLayout, BlogCard, useBlogSearch } from '../../components/blog';
import { getPostsByCategory } from '../../data/blog.generated';
import { BLOG_CATEGORIES } from '../../types/blog';
import { useDocumentTitle } from '../../hooks/core';

const POSTS_PER_PAGE = 6;

export const BlogCategoryPage: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [page, setPage] = useState(1);

  const categoryInfo = BLOG_CATEGORIES.find((c) => c.value === category);
  const categoryPosts = category ? getPostsByCategory(category) : [];
  const { query, setQuery, results } = useBlogSearch(categoryPosts);

  useDocumentTitle(categoryInfo ? `${categoryInfo.label} - Blog` : 'Blog');

  // Reset to page 1 when search query changes
  React.useEffect(() => {
    setPage(1);
  }, [query]);

  const allDisplayPosts = query ? results : categoryPosts;
  const totalPages = Math.ceil(allDisplayPosts.length / POSTS_PER_PAGE);
  const displayPosts = useMemo(() => {
    const startIndex = (page - 1) * POSTS_PER_PAGE;
    return allDisplayPosts.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [allDisplayPosts, page]);

  // Redirect if category doesn't exist (after all hooks)
  if (!categoryInfo) {
    return <Navigate to="/blog" replace />;
  }

  const pageDescription = `Browse all posts in the ${categoryInfo.label} category.`;

  return (
    <BlogLayout
      title={categoryInfo.label}
      description={pageDescription}
      breadcrumbs={[{ label: categoryInfo.label }]}
      showSearch
      onSearch={setQuery}
      searchValue={query}
    >
      <Helmet>
        <title>{categoryInfo.label} | SquareUp Blog</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={`${categoryInfo.label} - SquareUp Blog`} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://squareitup.app/blog/category/${category}`} />
        <meta property="og:site_name" content="SquareUp" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={`${categoryInfo.label} - SquareUp Blog`} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={`https://squareitup.app/blog/category/${category}`} />
      </Helmet>
      {displayPosts.length > 0 ? (
        <>
          <Grid container spacing={3}>
            {displayPosts.map((post) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.slug}>
                <BlogCard post={post} />
              </Grid>
            ))}
          </Grid>
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => {
                  setPage(value);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            textAlign: 'center',
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {query ? 'No posts found' : 'No posts in this category yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {query
              ? 'Try a different search term.'
              : 'Check back soon for new content!'}
          </Typography>
        </Paper>
      )}
    </BlogLayout>
  );
};

export default BlogCategoryPage;
